/**
 * Attendance: Zod CIDR refinement for trusted-network list validation.
 *
 * Validates IPv4 and IPv6 CIDR notations for the named trusted-network list
 * (CONTEXT D-13 / ATT-02). MUST-test per project conventions §Testing Philosophy —
 * Zod schema refinement is a public contract consumed by RHF forms across
 * the attendance policy screen.
 *
 * ATT-02 — IP/wifi CIDR allowlist
 */

import { z } from "zod";

/**
 * Validate an IPv4 CIDR block.
 *
 * Rules:
 * - Format: `A.B.C.D/prefix` where each octet is 0–255 and prefix is 0–32
 * - IPv4 addresses: four dot-separated octets (no leading zeros)
 */
function isValidIpv4Cidr(val: string): boolean {
  const match = val.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/([0-9]|[12][0-9]|3[0-2])$/);
  if (!match) return false;
  // Validate each octet is 0–255 (groups 1–4 are guaranteed by the regex)
  for (let i = 1; i <= 4; i++) {
    const octet = match[i];
    if (octet === undefined || parseInt(octet, 10) > 255) return false;
  }
  return true;
}

/**
 * Validate an IPv6 CIDR block.
 *
 * Rules:
 * - Format: `address/prefix` where prefix is 0–128
 * - Address: valid hex groups separated by colons (including :: shorthand)
 */
function isValidIpv6Cidr(val: string): boolean {
  const slashIdx = val.lastIndexOf("/");
  if (slashIdx === -1) return false;
  const address = val.slice(0, slashIdx);
  const prefix = val.slice(slashIdx + 1);
  // Prefix must be 0–128
  const prefixNum = parseInt(prefix, 10);
  if (isNaN(prefixNum) || prefixNum < 0 || prefixNum > 128) return false;
  // Basic IPv6 address validation: hex characters + colons + optional double-colon
  if (!/^[0-9a-fA-F:]+$/.test(address)) return false;
  if (address.includes(":::")) return false;
  // Must not be empty
  if (address.length === 0) return false;
  return true;
}

/**
 * Validate a CIDR string (IPv4 or IPv6).
 *
 * @param val - String to validate (e.g. "192.168.1.0/24" or "2001:db8::/32")
 * @returns true if the string is a valid IPv4 or IPv6 CIDR block
 */
export function isValidCidr(val: string): boolean {
  return isValidIpv4Cidr(val) || isValidIpv6Cidr(val);
}

/**
 * Zod schema for CIDR validation.
 *
 * Use in React Hook Form schemas for the trusted-network form fields.
 * Error message key follows i18n namespace convention (ATT-02 namespace = "attendance").
 *
 * @example
 * const networkSchema = z.object({
 *   name: z.string().min(1),
 *   cidr: cidrSchema,
 * });
 */
export const cidrSchema = z.string().refine((v) => isValidCidr(v), {
  message: "validation:attendance.network.cidr_invalid",
});
